import {useEffect, useRef, useState} from 'react';
import {ACCOUNT_ID} from '../../api/account';
import {createSubscription, getSubscription} from '../../api/payment';
import {commitTale, generateTaleText, getTale, getVoices} from '../../api/skazki';
import {getYandexClientId, trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {skazkiAudio, useQuietMode, useReducedMotion} from '../../utils/skazkiAudio';
import {
    ageDescription,
    ageWord,
    normalizeChildName,
    SKAZKI_GENERATION_STATUSES,
    SKAZKI_INTERESTS,
    SKAZKI_MORALS,
    SKAZKI_QUICK_NAMES,
} from '../../config/skazkiConstants';
import {
    Asterisk,
    BookIllustration,
    HeartIllustration,
    StarIllustration,
    VoiceAvatar,
} from './SkazkiIllustration';
import SkazkiPlayer from './SkazkiPlayer';
import SkazkiTaleBookText from './SkazkiTaleBookText';

const STORAGE_KEY = 'selebrus_skazki_draft';
const TALE_ID_STORAGE_KEY = 'selebrus_skazki_tale_id';
const SKAZKI_PLAN_ID = 'sub_monthly_lite';
// Shown until the live plan loads; the real charge always follows the DB plan.
const SKAZKI_FALLBACK_PRICE = 490;
const SKAZKI_FALLBACK_TALES = 6;
const WIZARD_STEPS = ['name', 'interests', 'moral'];
const DEFAULT_GENRE = 'adventure';
const ALL_STEPS = ['landing', ...WIZARD_STEPS, 'streaming', 'generating', 'paywall_pending', 'player'];

function loadDraft() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function saveDraft(d) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
    } catch {
    }
}

function clearDraft() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
    }
}

function loadSavedTaleId() {
    try {
        return localStorage.getItem(TALE_ID_STORAGE_KEY) || null;
    } catch {
        return null;
    }
}

function saveTaleId(id) {
    try {
        if (id) localStorage.setItem(TALE_ID_STORAGE_KEY, id);
    } catch {
    }
}

function clearSavedTaleId() {
    try {
        localStorage.removeItem(TALE_ID_STORAGE_KEY);
    } catch {
    }
}

export default function SkazkiCard({
                                       initialAction,
                                       initialTaleId,
                                       initialChildName,
                                       initialAge,
                                       skazkiCredits = 0,
                                       plans = {subscriptions: [], topups: []},
                                       subscription = null,
                                       onRefreshAccount,
                                   }) {
    const skazkiPlan = (plans.subscriptions || []).find(p => p.id === SKAZKI_PLAN_ID);
    const talesPerMonth = skazkiPlan?.tales || SKAZKI_FALLBACK_TALES;
    const planPrice = skazkiPlan?.price || SKAZKI_FALLBACK_PRICE;
    const draft = useRef(loadDraft()).current;
    const reducedMotion = useReducedMotion();
    const quietMode = useQuietMode();

    // Where to land when there's no live tale to restore: the saved wizard step
    // (if any), otherwise the landing screen.
    const resumeStep = () => {
        const s = draft?.step;
        if (s && ALL_STEPS.includes(s) && s !== 'generating' && s !== 'streaming' && s !== 'player') return s;
        return 'landing';
    };

    const [step, setStep] = useState(() => {
        if (initialTaleId) return 'player';
        if (initialAction === 'new') return 'name';
        // A saved tale (e.g. an unpaid preview) → open the player directly; the
        // restore effect below loads it and lands on the paywall, without
        // flashing the wizard first.
        if (loadSavedTaleId()) return 'player';
        return resumeStep();
    });

    const [childName, setChildName] = useState(initialChildName || draft?.childName || '');
    const [age, setAge] = useState(initialAge || draft?.age || 5);
    const [interests, setInterests] = useState(draft?.interests || []); // array of labels
    const [customInterest, setCustomInterest] = useState(draft?.customInterest || '');
    const [voiceId, setVoiceId] = useState(draft?.voiceId || null);
    const [tempoId, setTempoId] = useState(draft?.tempoId || 'normal');
    const [musicId, setMusicId] = useState(draft?.musicId || 'quiet');
    const [languageId, setLanguageId] = useState(draft?.languageId || 'ru');
    const [moral, setMoral] = useState(draft?.moral || '');
    const [customMoral, setCustomMoral] = useState(draft?.customMoral || '');

    const [taleId, setTaleId] = useState(initialTaleId || null);
    const [currentTale, setCurrentTale] = useState(null);
    // Optimistic "unlocked" flag flipped the moment the subscription activates,
    // before the refreshed credit count propagates down as a prop.
    const [subscriptionActivated, setSubscriptionActivated] = useState(false);
    // 'idle' | 'creating' | 'widget' | 'polling'
    const [payPhase, setPayPhase] = useState('idle');
    const [payToken, setPayToken] = useState(null);
    const payPollAbortRef = useRef(null);
    const [voices, setVoices] = useState([]);
    const [nameError, setNameError] = useState(null);
    const [statusIdx, setStatusIdx] = useState(0);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationError, setGenerationError] = useState(null);
    const [moderationError, setModerationError] = useState({interest: null, moral: null});
    const [playSampleVoice, setPlaySampleVoice] = useState(null);
    const sampleAudioRef = useRef(null);
    const pollAbortRef = useRef(null);

    const [streamedTitle, setStreamedTitle] = useState('');
    const [streamedText, setStreamedText] = useState('');
    const [firstParagraph, setFirstParagraph] = useState('');
    const [streamDone, setStreamDone] = useState(false);
    const [streamError, setStreamError] = useState(null);
    const [refineInstruction, setRefineInstruction] = useState('');
    // Monotonic token used to ignore results from a superseded/cancelled
    // generate request (e.g. the user hit "Изменить" or refined again).
    const streamReqRef = useRef(0);

    const progressIdx = WIZARD_STEPS.indexOf(step);
    const selectedVoice = voices.find(v => v.id === voiceId);
    const normalizedName = normalizeChildName(childName) || 'ребёнка';

    // Persist draft.
    useEffect(() => {
        if (step === 'landing' || step === 'player'
            || step === 'streaming' || step === 'generating'
            || step === 'paywall_pending') return;
        saveDraft({
            step, childName, age, interests, customInterest,
            voiceId, tempoId, musicId, languageId, moral, customMoral,
        });
    }, [step, childName, age, interests, customInterest, voiceId, tempoId, musicId, languageId, moral, customMoral]);

    // Load voices once.
    useEffect(() => {
        const orderVoices = (list) => {
            const idx = list.findIndex(v => v.id === 'friend');
            if (idx <= 0) return list;
            const copy = list.slice();
            const [first] = copy.splice(idx, 1);
            return [first, ...copy];
        };
        getVoices()
            .then(list => setVoices(orderVoices(list)))
            .catch(() => {
                setVoices(orderVoices([
                    {id: 'friend', name: 'Подружка', description: 'Лёгкий, дружеский', sample_url: ''},
                    {id: 'aunt', name: 'Тётя', description: 'Тёплый, заботливый', sample_url: ''},
                    {id: 'sister', name: 'Старшая сестра', description: 'Живой, близкий', sample_url: ''},
                    {id: 'uncle', name: 'Дядя', description: 'Уверенный, бархатный', sample_url: ''},
                    {id: 'dadfriend', name: 'Папин друг', description: 'Энергичный, открытый', sample_url: ''},
                ]));
            });
    }, []);

    useEffect(() => {
        if (voiceId || !voices.length) return;
        const preferred = voices.find(v => v.id === 'friend') || voices[0];
        if (preferred) setVoiceId(preferred.id);
    }, [voices, voiceId]);

    // The review step is a tall layout (story + refine + voice picker + CTAs).
    // After "Мне нравится — озвучить" we swap to the shorter `generating`
    // layout — on mobile the user's scroll position can end up below the new
    // content, so re-anchor to the top of the card whenever the step changes.
    useEffect(() => {
        if (step !== 'generating' && step !== 'player') return;
        const el = document.getElementById('sk-skazki-card-root');
        if (!el) return;
        try {
            el.scrollIntoView({behavior: 'smooth', block: 'start'});
        } catch {
            el.scrollIntoView();
        }
    }, [step]);

    useEffect(() => {
        if (step !== 'player' || !taleId) return;
        getTale(taleId).then(t => {
            if (t.status === 'processing') {
                setStreamedTitle(t.title || '');
                setStreamedText(t.text || '');
                setFirstParagraph(t.text || '');
                setStep('generating');
                return;
            }
            setCurrentTale(t);
            if (t.status === 'ready') skazkiAudio.loadTale(t);
        }).catch(() => setGenerationError('load'));
    }, [step, taleId]);

    // Restore an in-progress tale on mount (page refresh).
    useEffect(() => {
        if (initialTaleId || initialAction === 'new') return;
        const savedId = loadSavedTaleId();
        if (!savedId) return;
        let cancelled = false;
        getTale(savedId).then(t => {
            if (cancelled) return;
            if (t.status === 'ready') {
                setTaleId(savedId);
                setCurrentTale(t);
                skazkiAudio.loadTale(t);
                // Populate the review state so "Назад" from the paywall returns
                // to a filled-in review screen even after a page reload.
                setStreamedTitle(t.title || '');
                setStreamedText(t.text || '');
                setFirstParagraph(t.text || '');
                setStreamDone(true);
                setStep('player');
            } else if (t.status === 'processing') {
                setTaleId(savedId);
                setStreamedTitle(t.title || '');
                setStep('generating');
            } else if (t.status === 'preview_ready') {
                setTaleId(savedId);
                setStreamedTitle(t.title || '');
                setStreamedText(t.text || '');
                setFirstParagraph(t.text || '');
                setStreamDone(true);
                setCurrentTale(t);
                if (t.preview_url || t.audio_url) skazkiAudio.loadTale(t);
                setStep('player');
            } else if ((t.status === 'draft' || t.status === 'text_ready') && t.text) {
                setTaleId(savedId);
                setStreamedTitle(t.title || '');
                setStreamedText(t.text);
                setFirstParagraph(t.text);
                setStreamDone(true);
                setStep('streaming');
            } else {
                clearSavedTaleId();
                setStep(resumeStep());
            }
        }).catch(() => {
            if (cancelled) return;
            clearSavedTaleId();
            setStep(resumeStep());
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (step !== 'generating') return undefined;
        const t = setInterval(() => {
            setStatusIdx(i => Math.min(i + 1, SKAZKI_GENERATION_STATUSES.length - 1));
        }, 3500);
        return () => clearInterval(t);
    }, [step]);

    useEffect(() => {
        if (step !== 'generating') {
            setGenerationProgress(0);
            return undefined;
        }
        const start = Date.now();
        const t = setInterval(() => {
            const elapsed = (Date.now() - start) / 1000;
            // Fake progress capped at 92% until tale becomes ready.
            setGenerationProgress(Math.min(92, Math.round((elapsed / 8) * 92)));
        }, 250);
        return () => clearInterval(t);
    }, [step, taleId]);

    // Poll for the tale to become ready.
    useEffect(() => {
        // On the player step, poll only while the audio is actively recording —
        // stop once it's ready, preview-ready (waiting for payment), or failed.
        const playerRecording = step === 'player' && currentTale
            && currentTale.status !== 'ready'
            && currentTale.status !== 'preview_ready'
            && currentTale.status !== 'failed';
        const shouldPoll =
            (step === 'generating' || step === 'paywall_pending' || playerRecording)
            && taleId;
        if (!shouldPoll) return undefined;
        let cancelled = false;
        pollAbortRef.current = () => {
            cancelled = true;
        };
        (async () => {
            // Bounded so a backend stuck in `processing` (or a persistent error)
            // can't poll forever: ~200 × 1.5s ≈ 5 min, then surface a timeout.
            let polls = 0;
            while (!cancelled) {
                await new Promise(r => setTimeout(r, 1500));
                if (cancelled) return;
                if (++polls > 200) {
                    setGenerationError('failed');
                    showError('Озвучка занимает дольше обычного. Обновите страницу или попробуйте позже.');
                    return;
                }
                try {
                    const t = await getTale(taleId);
                    if (cancelled) return;
                    // NOTE: the backend maps both `preview_ready` and the real
                    // `ready` to public status 'ready'; `paid` is what tells a
                    // locked preview apart from a fully-voiced tale.
                    if (t.status === 'ready' && t.paid) {
                        setCurrentTale(t);
                        setGenerationProgress(100);
                        skazkiAudio.loadTale(t);
                        // Small pause for the 100% flash before cross-fade.
                        await new Promise(r => setTimeout(r, reducedMotion ? 0 : 350));
                        if (!cancelled) {
                            setStep('player');
                            clearDraft();
                            clearSavedTaleId();
                        }
                        return;
                    }
                    if (t.status === 'ready' || t.preview_url) {
                        // Preview audio finished recording — surface it so the
                        // paywall's play button activates, then stop polling and
                        // wait for the user to pay. Keep the draft/tale-id so a
                        // page reload restores the paywall instead of resetting.
                        setCurrentTale(t);
                        skazkiAudio.loadTale(t);
                        if (!cancelled) setStep('player');
                        return;
                    }
                    if (t.status === 'failed') {
                        setGenerationError('failed');
                        return;
                    }
                } catch (err) {
                    if (err.status === 0 || !navigator.onLine) setGenerationError('network');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, taleId, reducedMotion, currentTale?.status]);

    // Keyboard: Enter = forward, Esc = back (on input screens only).
    useEffect(() => {
        const onKey = (e) => {
            if (['landing', 'generating', 'paywall_pending', 'player'].includes(step)) return;
            if (e.key === 'Escape') {
                e.preventDefault();
                goBack();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                if (e.target?.tagName === 'TEXTAREA') return;
                if (canAdvance()) {
                    e.preventDefault();
                    goForward();
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, childName, age, interests, voiceId]);

    // ---------- Actions ----------
    const startWizard = () => {
        trackEvent('skazki_wizard_start');
        setStep('name');
    };

    const startOver = () => {
        clearDraft();
        clearSavedTaleId();
        setTaleId(null);
        setCurrentTale(null);
        setStreamedTitle('');
        setStreamedText('');
        setFirstParagraph('');
        setStreamDone(false);
        setStreamError(null);
        setChildName('');
        setAge(5);
        setInterests([]);
        setCustomInterest('');
        setVoiceId(null);
        setTempoId('normal');
        setMusicId('quiet');
        setLanguageId('ru');
        setMoral('');
        setCustomMoral('');
        setStep('name');
    };

    const goBack = () => {
        const i = WIZARD_STEPS.indexOf(step);
        if (i > 0) setStep(WIZARD_STEPS[i - 1]); else setStep('landing');
    };

    const canAdvance = () => {
        if (step === 'name') return normalizeChildName(childName).length >= 1 && age >= 3 && age <= 12;
        if (step === 'interests') return true;
        if (step === 'moral') return true;
        return false;
    };

    const goForward = async () => {
        if (step === 'name') {
            const clean = normalizeChildName(childName);
            if (!clean) {
                setNameError('Без имени сказка будет не такой волшебной');
                return;
            }
            setChildName(clean);
            setNameError(null);
            setStep('interests');
        } else if (step === 'interests') {
            const custom = customInterest.trim();
            if (custom && custom.length < 2) {
                setModerationError(e => ({...e, interest: 'Давайте выберем что-то другое'}));
                return;
            }
            setStep('moral');
        } else if (step === 'moral') {
            const customMoralText = customMoral.trim();
            if (customMoralText && customMoralText.length < 2) {
                setModerationError(e => ({...e, moral: 'Давайте выберем что-то другое'}));
                return;
            }
            trackEvent('skazki_create_clicked');
            await kickOffStreaming();
        }
    };


    const kickOffStreaming = async ({instruction} = {}) => {
        const reqId = ++streamReqRef.current;

        if (!instruction) setStreamedTitle('');
        setStreamedText('');
        setFirstParagraph('');
        setStreamDone(false);
        setStreamError(null);
        setGenerationError(null);
        setCurrentTale(null);

        const allInterests = [...interests.map(i => typeof i === 'string' ? i : i.label)];
        if (customInterest.trim()) allInterests.push(customInterest.trim());
        const finalMoral = (customMoral.trim() || moral).trim() || null;
        const payload = {
            taleId, accountId: ACCOUNT_ID,
            childName: normalizeChildName(childName),
            age, interests: allInterests,
            genre: DEFAULT_GENRE, voice: voiceId, tempo: tempoId,
            moral: finalMoral,
            music: musicId === 'none' ? null : musicId,
            language: languageId,
            instruction: instruction || null,
        };
        setStep('streaming');

        try {
            const tale = await generateTaleText(payload);
            if (streamReqRef.current !== reqId) return; // superseded or cancelled
            setTaleId(tale.id);
            saveTaleId(tale.id);
            setStreamedTitle(tale.title || '');
            setStreamedText(tale.text || '');
            setFirstParagraph(tale.text || '');
            setStreamDone(true);
        } catch {
            if (streamReqRef.current !== reqId) return;
            setStreamError(navigator.onLine === false ? 'network' : 'failed');
        }
    };

    const stopAndEdit = () => {
        streamReqRef.current++; // ignore any in-flight result
        setStep('moral');
    };

    // Single in-flight guard shared by every commit path (preview, post-payment
    // unlock, accidental double-click). The backend's commit is NOT idempotent —
    // two concurrent commits on the same tale can double-charge a credit — so we
    // never let two run at once. Returns null when a commit is already running.
    const commitInFlightRef = useRef(false);
    const commitOnce = async (id, voice) => {
        if (commitInFlightRef.current) return null;
        commitInFlightRef.current = true;
        try {
            return await commitTale(id, voice ? {voice} : {});
        } finally {
            commitInFlightRef.current = false;
        }
    };

    const approveAndCommit = async () => {
        if (!taleId) return;
        trackEvent('skazki_commit');
        setGenerationError(null);
        // No full-screen loader: jump straight to the player/paywall with a
        // placeholder tale. The audio records in the background — the player
        // shows a "recording" state and swaps in the clip once it's ready.
        const interestLabels = interests.map(i => (typeof i === 'string' ? i : i.label));
        setCurrentTale(prev => (prev && prev.id === taleId ? prev : {
            id: taleId,
            status: 'processing',
            title: streamedTitle || `Сказка для ${normalizedName}`,
            text: streamedText || firstParagraph || '',
            child_name: normalizedName,
            age,
            interests: interestLabels,
            genre: DEFAULT_GENRE,
            paid: false,
        }));
        setStep('player');
        try {
            const r = await commitOnce(taleId, voiceId);
            if (!r) return; // a commit is already in flight (double-click) — let it finish
            setCurrentTale(prev => ({...(prev || {}), ...r}));
            if (r.preview_url || r.audio_url) skazkiAudio.loadTale(r);
            // A locked preview also reports status 'ready' (backend maps
            // preview_ready→ready); only a PAID, fully-voiced tale is truly done.
            // Keep the draft/tale-id for previews so a reload on the paywall
            // restores it instead of dropping the user back to the start.
            if (r.status === 'ready' && r.paid) {
                clearDraft();
                clearSavedTaleId();
            }
        } catch {
            // Couldn't start the recording — return to the review screen so the
            // user can retry, with their text preserved.
            showError(navigator.onLine === false
                ? 'Нет связи. Проверьте интернет и попробуйте ещё раз.'
                : 'Не удалось записать озвучку. Попробуйте ещё раз.');
            setStep('streaming');
        }
    };

    const cancelGeneration = () => {
        pollAbortRef.current?.();
        setStep('moral');
    };

    const retry = async () => {
        setGenerationError(null);
        setStatusIdx(0);
        setGenerationProgress(0);
        kickOffStreaming();
    };

    const submitRefine = () => {
        const trimmed = refineInstruction.trim();
        if (!trimmed || !taleId) return;
        setRefineInstruction('');
        kickOffStreaming({instruction: trimmed});
    };

    const handlePlaySample = (voice) => {
        if (playSampleVoice === voice.id) {
            sampleAudioRef.current?.pause();
            setPlaySampleVoice(null);
            return;
        }
        sampleAudioRef.current?.pause();
        if (!voice.sample_url) return;
        const a = new Audio(voice.sample_url);
        sampleAudioRef.current = a;
        a.play().catch(() => setPlaySampleVoice(null));
        setPlaySampleVoice(voice.id);
        a.onended = () => setPlaySampleVoice(null);
    };

    const toggleInterest = (label) => {
        setInterests(prev => {
            const arr = prev.map(x => typeof x === 'string' ? x : x.label);
            if (arr.includes(label)) return arr.filter(x => x !== label);
            if (arr.length >= 5) return arr;
            return [...arr, label];
        });
    };
    const isInterestOn = (label) => interests.map(x => typeof x === 'string' ? x : x.label).includes(label);
    const interestCount = interests.length;

    if (step === 'player' && currentTale
        && (currentTale.status === 'ready' || currentTale.preview_url || currentTale.audio_url || currentTale.text)) {
        const handleUnlock = () => {
            if (skazkiCredits > 0) approveAndCommit();
        };
        const handleSubscribe = async () => {
            if (payPhase !== 'idle') return;
            trackEvent('skazki_subscription_started');
            setPayPhase('creating');
            try {
                const yandexClientId = await getYandexClientId();
                const r = await createSubscription({
                    accountId: ACCOUNT_ID,
                    planId: SKAZKI_PLAN_ID,
                    yandexClientId,
                });
                if (!r?.confirmation_token) throw new Error('No confirmation token');
                setPayToken(r.confirmation_token);
                setPayPhase('widget');
            } catch (err) {
                trackEvent('skazki_subscription_error', {message: err?.message || 'unknown'});
                const msg = err?.data?.detail
                    || 'Не удалось оформить подписку. Попробуйте ещё раз.';
                showError(msg);
                setPayPhase('idle');
            }
        };
        const handleCloseModal = () => {
            setPayPhase('idle');
            setPayToken(null);
            payPollAbortRef.current?.();
        };
        const handleWidgetSuccess = () => {
            setPayPhase('polling');
            let cancelled = false;
            payPollAbortRef.current = () => {
                cancelled = true;
            };
            (async () => {
                // Wait ~30s for the webhook to flip the subscription to active —
                // which also grants this month's skazki credits.
                for (let i = 0; i < 20 && !cancelled; i++) {
                    try {
                        const sub = await getSubscription(ACCOUNT_ID);
                        if (sub && sub.status === 'active') break;
                    } catch {
                        // keep polling
                    }
                    await new Promise(r => setTimeout(r, 1500));
                }
                if (cancelled) return;
                trackEvent('skazki_subscription_active');
                setSubscriptionActivated(true);
                setPayPhase('idle');
                setPayToken(null);
                onRefreshAccount?.();
                // Spend one of the freshly granted credits to unlock the exact
                // tale the user previewed, then poll until the full audio lands
                // and swap the player from the preview clip to the real audio.
                const tid = currentTale?.id || taleId;
                const commitVoice = voiceId;
                if (!tid) return;
                // The webhook applies the subscription credits, but that can lag
                // a beat behind `status: active`. Retry the commit so the unlock
                // survives that race instead of silently stranding the user on a
                // hidden paywall with a preview that never upgrades.
                let committed = false;
                for (let attempt = 0; attempt < 5 && !cancelled && !committed; attempt++) {
                    if (attempt > 0) await new Promise(r => setTimeout(r, 2500));
                    try {
                        // null => the preview commit is still in flight; wait and
                        // retry so we never issue two concurrent commits.
                        const r = await commitOnce(tid, commitVoice);
                        if (r) committed = true;
                    } catch {
                        // retry
                    }
                }
                if (cancelled) return;
                if (!committed) {
                    // Could not kick off the full audio. Surface it and fall back
                    // to the credits-based "generate full tale" CTA in the player
                    // (skazkiCredits will have propagated via onRefreshAccount).
                    trackEvent('skazki_unlock_commit_failed');
                    showError('Подписка активна, но сказку не удалось открыть автоматически. Нажмите «Сгенерировать полную сказку».');
                    return;
                }
                for (let i = 0; i < 60 && !cancelled; i++) {
                    try {
                        const t = await getTale(tid);
                        if (t) setCurrentTale(t);
                        if (t?.status === 'ready' && (t.audio_url || t.preview_url)) {
                            skazkiAudio.loadTale(t);
                            return;
                        }
                    } catch {
                        // keep polling
                    }
                    await new Promise(r => setTimeout(r, 2000));
                }
            })();
        };
        return (
            <>
                <SkazkiPlayer
                    tale={currentTale}
                    quietMode={quietMode}
                    skazkiCredits={skazkiCredits}
                    unlockedOptimistic={subscriptionActivated}
                    subscriptionActive={subscription?.status === 'active'}
                    subscribing={payPhase !== 'idle'}
                    talesPerMonth={talesPerMonth}
                    planPrice={planPrice}
                    onUnlock={handleUnlock}
                    onSubscribe={handleSubscribe}
                    onCreateAnother={() => {
                        setInterests([]);
                        setCustomInterest('');
                        setVoiceId(null);
                        setTempoId('normal');
                        setMusicId('quiet');
                        setMoral('');
                        setCustomMoral('');
                        setCurrentTale(null);
                        setTaleId(null);
                        setStep('interests');
                    }}
                    onBack={() => setStep('streaming')}
                />
                {(payPhase === 'widget' || payPhase === 'polling') && (
                    <PaymentModal
                        token={payToken}
                        phase={payPhase}
                        planPrice={planPrice}
                        onClose={handleCloseModal}
                        onSuccess={handleWidgetSuccess}
                        onFail={() => {
                            showError('Оплата не прошла. Попробуйте ещё раз.');
                            handleCloseModal();
                        }}
                    />
                )}
            </>
        );
    }

    if (step === 'player') {
        return (
            <div className="max-w-[720px] mx-auto px-4 py-16 text-center">
                <div
                    className="inline-block w-10 h-10 border-2 border-gray-200 border-t-[#FF6464] rounded-full animate-spin"/>
                <p className="text-[#3D3D3D]/80 mt-4">
                    {currentTale?.status === 'processing' ? 'Озвучиваем сказку…' : 'Загружаем сказку…'}
                </p>
            </div>
        );
    }

    // ============ LANDING ============
    if (step === 'landing') {
        const hasDraft = !!draft && draft.step && !['landing', 'generating', 'player'].includes(draft.step);
        return (
            <Shell>
                <div className="flex flex-col items-center text-center px-6 pt-4 pb-8 md:pt-10">
                    <div className="flex justify-center mb-2">
                        <BookIllustration className="w-[180px] h-[150px] md:w-[220px] md:h-[180px]"/>
                    </div>
                    <h1 className="text-[40px] md:text-[56px] leading-[1.02] font-bold text-[#0E0E0E] tracking-[-0.02em] mt-4">
                        Сказка<br/>для вашего<br/>ребёнка
                    </h1>
                    <p className="mt-4 text-sm md:text-base text-[#3D3D3D]/90 leading-relaxed max-w-[360px]">
                        Мы придумаем историю, озвучим её и соберём в&nbsp;аудио-книгу за&nbsp;пару минут
                    </p>

                    <div className="w-full max-w-[360px] mt-8 flex flex-col gap-3">
                        {hasDraft ? (
                            <>
                                <button onClick={startWizard}
                                        className="sk-btn sk-btn--ink h-[56px] text-[20px] w-full">
                                    Продолжить{draft.childName ? ` про ${draft.childName}` : ''} →
                                </button>
                                <button onClick={startOver}
                                        className="text-sm text-[#3D3D3D]/80 hover:text-[#0E0E0E] transition">
                                    Начать заново
                                </button>
                            </>
                        ) : (
                            <button onClick={startWizard} className="sk-btn sk-btn--ink h-[56px] text-[20px] w-full">
                                Начать сказку →
                            </button>
                        )}
                    </div>

                    <div className="mt-6 w-full max-w-[360px]">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#9A9AA2] mb-2 text-center">
                            язык сказки
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setLanguageId('ru')}
                                aria-pressed={languageId === 'ru'}
                                className={`sk-option-card text-left ${languageId === 'ru' ? 'sk-option-card--on' : ''}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">🇷🇺</span>
                                    <span className="font-bold text-[#0E0E0E] text-[14px]">Русский</span>
                                </div>
                                <div className="text-[10px] text-[#9A9AA2] mt-1">по умолчанию</div>
                            </button>
                            <button
                                onClick={() => setLanguageId('en')}
                                aria-pressed={languageId === 'en'}
                                className={`sk-option-card text-left ${languageId === 'en' ? 'sk-option-card--on' : ''}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">🇬🇧</span>
                                    <span className="font-bold text-[#0E0E0E] text-[14px]">English</span>
                                </div>
                                <div className="text-[10px] text-[#FF6464] mt-1">для изучения языка</div>
                            </button>
                        </div>
                    </div>

                    <div className="mt-5 text-xs text-[#9A9AA2] leading-relaxed">
                        <div><Asterisk className="text-[#FF6464] mr-1"/> без регистрации</div>
                        <div><Asterisk className="text-[#FF6464] mr-1"/> готовая сказка за 60 секунд</div>
                    </div>

                </div>
            </Shell>
        );
    }

    // ============ Shared header ============
    const hasGeneratedText = streamDone && streamedText && taleId;
    const stepHeader = (
        <div className="flex items-center gap-3 px-5 md:px-8 py-3 border-b border-[#F6F6F6]">
            <button
                onClick={goBack}
                aria-label="Назад"
                className="w-9 h-9 rounded-full border-[1.5px] border-[#0E0E0E] text-[#0E0E0E] flex items-center justify-center text-lg hover:bg-[#F6F6F6] transition flex-shrink-0"
            >←
            </button>
            <div className="flex-1">
                <div className="flex justify-between text-[11px] text-[#3D3D3D]/80 mb-1">
                    <span>шаг {progressIdx + 1} из {WIZARD_STEPS.length}</span>
                </div>
                <div className="h-[3px] rounded-full bg-[#F6F6F6] overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                            width: `${((progressIdx + 1) / WIZARD_STEPS.length) * 100}%`,
                            background: 'linear-gradient(90deg, #FF6464, #FFCFCC)',
                        }}
                    />
                </div>
            </div>
            {hasGeneratedText && (
                <button
                    onClick={() => setStep('streaming')}
                    className="text-[12px] md:text-[13px] text-[#0E0E0E] underline underline-offset-2 hover:text-[#FF6464] transition flex-shrink-0"
                >
                    к готовой сказке →
                </button>
            )}
        </div>
    );

    // ============ NAME + AGE ============
    if (step === 'name') {
        return (
            <Shell>
                {stepHeader}
                <Split
                    heading="Для кого сказка?"
                    subtitle="Имя и возраст появятся в истории"
                    illustration={<HeartIllustration className="w-[130px] h-[110px] md:w-[260px] md:h-[220px]"/>}
                >
                    <div className="sk-card-elevated px-5 py-4">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#9A9AA2] mb-1">имя</div>
                        <div className="relative">
                            <input
                                type="text" value={childName} autoFocus autoCapitalize="words" autoComplete="off"
                                maxLength={24}
                                onChange={e => {
                                    setChildName(e.target.value);
                                    setNameError(null);
                                }}
                                className="w-full bg-transparent text-[28px] font-semibold text-[#0E0E0E] outline-none border-b-[1.5px] border-[#F6F6F6] focus:border-transparent pb-1"
                                aria-label="Имя ребёнка"
                                placeholder="Маша"
                            />
                            <span
                                className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-[#FF6464] to-[#FFCFCC] scale-x-0 origin-left transition-transform duration-300 skazki-focus-underline"/>
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="text-[11px] text-[#9A9AA2] mb-2">быстрый выбор</div>
                        <div className="flex flex-wrap gap-2">
                            {SKAZKI_QUICK_NAMES.map(n => {
                                const on = childName === n;
                                return (
                                    <button
                                        key={n}
                                        onClick={() => {
                                            setChildName(n);
                                            setNameError(null);
                                        }}
                                        className={`sk-chip ${on ? 'sk-chip--on' : ''}`}
                                        aria-pressed={on}
                                    >{n}</button>
                                );
                            })}
                        </div>
                    </div>

                    {nameError && <p className="mt-3 text-sm text-[#E53935]">{nameError}</p>}

                    <div className="mt-6 sk-card-elevated px-5 py-4">
                        <div className="text-[10px] uppercase tracking-[0.1em] text-[#9A9AA2] mb-2">возраст</div>
                        <div className="flex items-center justify-center gap-6 md:gap-10">
                            <button
                                onClick={() => setAge(a => Math.max(3, a - 1))}
                                aria-label="Уменьшить возраст"
                                className="w-10 h-10 md:w-12 md:h-12 rounded-full border-[1.5px] border-[#0E0E0E] text-[#3D3D3D] flex items-center justify-center text-[20px] md:text-[24px] hover:bg-[#F6F6F6] transition"
                            >−
                            </button>
                            <div className="text-[#0E0E0E] font-bold leading-none tabular-nums text-[64px] md:text-[88px]"
                                 aria-label={`${age} лет`}>
                                {age}
                            </div>
                            <button
                                onClick={() => setAge(a => Math.min(12, a + 1))}
                                aria-label="Увеличить возраст"
                                className="w-10 h-10 md:w-12 md:h-12 rounded-full border-[1.5px] border-[#0E0E0E] text-[#0E0E0E] flex items-center justify-center text-[20px] md:text-[24px] bg-[#FFCFCC] hover:brightness-95 transition"
                            >+
                            </button>
                        </div>
                        <div className="text-center italic text-[#3D3D3D]/80 text-sm md:text-base mt-1">{ageWord(age)}</div>
                        <div className="text-xs text-[#3D3D3D]/80 leading-snug mt-2 text-center">
                            {ageDescription(age)}.
                        </div>
                    </div>

                    <CtaRow>
                        <button onClick={goForward} disabled={!canAdvance()}
                                className="sk-btn sk-btn--gold h-[52px] px-8 text-[18px] w-full md:w-auto">
                            Дальше →
                        </button>
                    </CtaRow>
                </Split>
            </Shell>
        );
    }

    // ============ INTERESTS ============
    if (step === 'interests') {
        const atMax = interestCount >= 5;
        return (
            <Shell>
                {stepHeader}
                <Split
                    heading={`Что ${normalizedName} любит?`}
                    subtitle="Выберите 2–5, это появится в сюжете"
                    illustration={<StarIllustration className="w-[110px] h-[110px] md:w-[240px] md:h-[240px]"/>}
                >
                    <div className="flex flex-wrap gap-2">
                        {SKAZKI_INTERESTS.map(({emoji, label}) => {
                            const on = isInterestOn(label);
                            const blocked = !on && atMax;
                            return (
                                <button
                                    key={label}
                                    onClick={() => toggleInterest(label)}
                                    aria-pressed={on}
                                    disabled={blocked}
                                    className={`sk-chip ${on ? 'sk-chip--on' : ''} ${blocked ? 'opacity-40 cursor-not-allowed' : ''}`}
                                >
                                    <span className="text-base">{emoji}</span>
                                    <span>{label}</span>
                                    {on && <span className="text-[10px] ml-0.5">✓</span>}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="mt-5 rounded-[12px] border-[1.5px] border-dashed border-[#9A9AA2] bg-white px-4 py-3">
                        <label className="block text-[10px] uppercase tracking-[0.1em] text-[#9A9AA2]">или своё</label>
                        <input
                            type="text"
                            value={customInterest}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\p{Emoji_Presentation}/gu, '').slice(0, 50);
                                setCustomInterest(v);
                                setModerationError(s => ({...s, interest: null}));
                            }}
                            placeholder="например, бабушкин сад"
                            className="mt-1 w-full bg-transparent outline-none text-sm italic text-[#0E0E0E] placeholder:text-[#9A9AA2]"
                        />
                        {moderationError.interest &&
                            <p className="mt-1 text-xs text-[#E53935] not-italic">{moderationError.interest}</p>}
                    </div>

                    <CtaRow>
                        <span className="text-xs text-[#3D3D3D]/80">
                            выбрано <b className={atMax ? 'text-[#FF6464]' : 'text-[#0E0E0E]'}>{interestCount}</b>
                        </span>
                        <button onClick={goForward} className="sk-btn sk-btn--gold h-[52px] px-8 text-[18px]">
                            Дальше →
                        </button>
                    </CtaRow>
                </Split>
            </Shell>
        );
    }

    // ============ MORAL + SUMMARY ============
    if (step === 'moral') {
        const moralLabel = (customMoral.trim() || moral || '').toLowerCase() || 'на усмотрение';
        return (
            <Shell>
                {stepHeader}
                <div className="px-5 pt-6 pb-6 md:px-16 md:pt-12 md:max-w-[840px] md:mx-auto">
                    <h2 className="text-[32px] md:text-[44px] font-bold text-[#0E0E0E] leading-[1.05] tracking-[-0.02em]">
                        Чему учит сказка?
                    </h2>
                    <p className="text-[13px] md:text-[15px] text-[#3D3D3D]/80 mt-1">Необязательно — можно
                        пропустить</p>

                    <div className="flex flex-wrap gap-[6px] mt-5">
                        {SKAZKI_MORALS.map(m => {
                            const on = moral === m;
                            return (
                                <button
                                    key={m}
                                    onClick={() => {
                                        setMoral(on ? '' : m);
                                        setCustomMoral('');
                                    }}
                                    aria-pressed={on}
                                    className={`sk-chip ${on ? 'sk-chip--on' : ''}`}
                                >{m.toLowerCase()}</button>
                            );
                        })}
                    </div>

                    <div className="mt-4">
                        <input
                            type="text" value={customMoral} placeholder="или свой вариант"
                            onChange={(e) => {
                                setCustomMoral(e.target.value.slice(0, 80));
                                if (e.target.value) setMoral('');
                                setModerationError(s => ({...s, moral: null}));
                            }}
                            className="w-full px-4 py-3 rounded-[12px] border-[1.5px] border-dashed border-[#9A9AA2] bg-white text-sm text-[#0E0E0E] placeholder:text-[#9A9AA2] italic outline-none focus:border-[#FF6464]"
                        />
                        {moderationError.moral &&
                            <p className="mt-1 text-xs text-[#E53935]">{moderationError.moral}</p>}
                    </div>

                    {/* Summary card */}
                    <div className="sk-card-elevated mt-6 px-5 py-5">
                        <div className="font-bold text-[#FF6464] text-[18px] md:text-[22px] mb-2">
                            <Asterisk/> Ваша сказка
                        </div>
                        <div className="text-[13px] md:text-[14px] leading-[1.75]">
                            <SummaryRow label="для" value={`${normalizedName}, ${age} лет`}
                                        onEdit={() => setStep('name')}/>
                            <SummaryRow label="любит"
                                        value={interests.slice(0, 3).map(i => typeof i === 'string' ? i : i.label).join(', ') + (customInterest.trim() ? (interestCount ? `, ${customInterest.trim()}` : customInterest.trim()) : '') || '—'}
                                        onEdit={() => setStep('interests')}/>
                            <SummaryRow label="учит" value={moralLabel} onEdit={() => {
                            }}/>
                            <SummaryRow
                                label="язык"
                                value={languageId === 'en' ? 'english · для изучения языка' : 'русский'}
                                onEdit={() => setLanguageId(languageId === 'en' ? 'ru' : 'en')}
                                last
                            />
                        </div>
                    </div>

                    <div className="mt-7 flex flex-col items-stretch gap-2">
                        <button onClick={goForward} className="sk-btn sk-btn--ink h-[60px] text-[22px] w-full">
                            <Asterisk/> Создать сказку
                        </button>
                        <div className="text-center text-[11px] text-[#9A9AA2]">генерация займёт ~30 секунд</div>
                    </div>
                </div>
            </Shell>
        );
    }

    // ============ STREAMING (text) ============
    if (step === 'streaming') {
        const canApprove = streamDone && !streamError;
        return (
            <Shell>
                <div className="flex items-center justify-between gap-3 px-5 md:px-8 py-3 border-b border-[#F6F6F6]">
                    <button
                        onClick={stopAndEdit}
                        aria-label="Назад"
                        className="w-9 h-9 rounded-full border-[1.5px] border-[#0E0E0E] text-[#0E0E0E] flex items-center justify-center text-lg hover:bg-[#F6F6F6] transition flex-shrink-0"
                    >←
                    </button>
                    <button
                        onClick={startOver}
                        className="text-[12px] md:text-[13px] text-[#3D3D3D]/80 hover:text-[#0E0E0E] underline underline-offset-2 transition"
                    >
                        Начать заново
                    </button>
                </div>
                <div className="md:grid md:grid-cols-[minmax(420px,_460px)_1fr] md:min-h-[580px]">
                    <div
                        className="sk-panel-deep flex flex-col items-center justify-center px-6 py-8 md:px-14 md:py-12">
                        <div className="text-[11px] uppercase tracking-[0.15em] text-[#3D3D3D]/60">Название</div>
                        <div
                            className="mt-3 font-bold text-[#0E0E0E] text-[22px] md:text-[32px] text-center leading-[1.1]">
                            {streamedTitle || '…'}
                        </div>
                        <div className="mt-6 text-[12px] text-[#3D3D3D]/80 text-center">
                            {streamError ? 'ошибка — попробуйте ещё раз'
                                : streamDone ? 'готово — прочтите первый абзац'
                                    : firstParagraph ? 'дописываем сказку…'
                                        : 'придумываем сказку…'}
                        </div>
                    </div>

                    <div className="px-5 py-6 md:px-16 md:py-12 flex flex-col min-h-0">
                        <SkazkiTaleBookText text={streamedText} title={streamedTitle}/>

                        {canApprove && (
                            <div className="mt-3 text-[12px] italic text-[#3D3D3D]/60 text-center">
                                Это начало сказки. Полный текст будет создан во время озвучки.
                            </div>
                        )}

                        {streamError ? (
                            <div className="mt-4 text-[13px] text-[#E53935]">
                                Не получилось сгенерировать. Попробуйте ещё раз.
                            </div>
                        ) : null}

                        {canApprove && (
                            <div className="mt-5">
                                <label
                                    className="block text-[11px] uppercase tracking-[0.15em] text-[#3D3D3D]/60 mb-2">
                                    Хотите переписать? опишите пожелания
                                </label>
                                <div className="flex flex-col md:flex-row gap-2">
                                    <textarea
                                        value={refineInstruction}
                                        onChange={(e) => setRefineInstruction(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                                e.preventDefault();
                                                submitRefine();
                                            }
                                        }}
                                        maxLength={280}
                                        rows={2}
                                        placeholder="например: добавь щенка, сделай смешнее, убери волка"
                                        className="flex-1 resize-none rounded-[12px] border border-[#E5E5E5] bg-white px-3 py-2 text-[14px] text-[#0E0E0E] placeholder:text-[#3D3D3D]/40 focus:outline-none focus:border-[#FF6464]"
                                    />
                                    <button
                                        onClick={submitRefine}
                                        disabled={!refineInstruction.trim()}
                                        className="sk-btn sk-btn--ghost h-[52px] md:w-[160px] text-[15px] disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Переписать
                                    </button>
                                </div>
                            </div>
                        )}

                        {canApprove && (
                            <div className="mt-5">
                                <label
                                    className="block text-[11px] uppercase tracking-[0.15em] text-[#3D3D3D]/60 mb-2">
                                    Выберите голос
                                </label>
                                <div className="grid grid-cols-5 gap-[6px]">
                                    {voices.map(v => {
                                        const on = voiceId === v.id;
                                        const playing = playSampleVoice === v.id;
                                        return (
                                            <button
                                                key={v.id}
                                                onClick={() => setVoiceId(v.id)}
                                                aria-pressed={on}
                                                className={`relative sk-option-card flex flex-col items-center text-center py-[10px] px-[4px] ${on ? 'sk-option-card--on' : ''}`}
                                            >
                                                <span
                                                    role={v.sample_url ? 'button' : undefined}
                                                    tabIndex={v.sample_url ? 0 : undefined}
                                                    aria-label={v.sample_url ? 'Прослушать образец' : undefined}
                                                    onClick={(e) => {
                                                        if (!v.sample_url) return;
                                                        e.stopPropagation();
                                                        handlePlaySample(v);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (v.sample_url && (e.key === 'Enter' || e.key === ' ')) {
                                                            e.stopPropagation();
                                                            handlePlaySample(v);
                                                        }
                                                    }}
                                                    className="mb-1 cursor-pointer"
                                                >
                                                    <VoiceAvatar voiceId={v.id} active={on} playing={playing}/>
                                                </span>
                                                <div className="text-[11px] font-semibold">{v.name}</div>
                                                <div className="text-[9px] text-[#9A9AA2] mt-[1px]">3 сек</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="mt-5 flex flex-col md:flex-row gap-2 md:gap-3">
                            <button
                                onClick={approveAndCommit}
                                disabled={!canApprove || !voiceId}
                                className="sk-btn sk-btn--ink h-[52px] flex-1 text-[18px] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                            >
                                {!streamDone && !streamError ? (
                                    <>
                                        <span
                                            className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>
                                        Дописываем…
                                    </>
                                ) : (
                                    'Мне нравится — озвучить'
                                )}
                            </button>
                            <button
                                onClick={stopAndEdit}
                                className="sk-btn sk-btn--ghost h-[52px] flex-1 text-[16px]"
                            >
                                Изменить
                            </button>
                        </div>
                    </div>
                </div>
            </Shell>
        );
    }

    // ============ GENERATING ============
    if (step === 'generating') {
        return (
            <Shell>
                <div className="md:min-h-[580px]">
                    {/* Story + progress */}
                    <div className="px-5 py-6 md:px-16 md:py-12 flex flex-col min-h-0">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div
                                    className="font-bold text-[#0E0E0E] text-[22px] md:text-[36px] leading-[1.05] tracking-[-0.01em]">
                                    {streamedTitle || `Сказка для ${normalizedName}`}
                                </div>
                                <div className="text-[12px] md:text-[13px] text-[#3D3D3D]/80 mt-1">
                                    Глава 1
                                </div>
                            </div>
                            <div
                                className="font-bold text-[#FF6464] text-[28px] md:text-[40px] tabular-nums leading-none">
                                {generationProgress}%
                            </div>
                        </div>

                        {generationError ? (
                            <ErrorBlock kind={generationError} onRetry={retry} onBack={() => setStep('moral')}/>
                        ) : (
                            <>
                                <div className="mt-4 flex-1 flex flex-col min-h-0">
                                    <SkazkiTaleBookText text={firstParagraph || streamedText}/>
                                    <div className="mt-3 text-[12px] italic text-[#3D3D3D]/60 text-center">
                                        Это начало сказки. Полный текст будет создан во время озвучки.
                                    </div>
                                </div>

                                <div
                                    className="sk-card-elevated mt-4 md:mt-6 flex items-center gap-3 px-4 py-[10px] rounded-full">
                                    <span className="text-lg">🎙</span>
                                    <span
                                        className="text-[12px] md:text-[13px] text-[#3D3D3D]/80 whitespace-nowrap">{SKAZKI_GENERATION_STATUSES[statusIdx]}</span>
                                    <div className="flex-1 h-[6px] rounded-full bg-[#F6F6F6] overflow-hidden relative">
                                        <div
                                            className="absolute inset-y-0 left-0 bg-[#FF6464] rounded-full transition-all"
                                            style={{width: `${generationProgress}%`}}/>
                                    </div>
                                    <span
                                        className="font-bold text-[#FF6464] text-sm tabular-nums">{generationProgress}%</span>
                                </div>

                                <div className="mt-5 flex md:hidden justify-center">
                                    <button onClick={cancelGeneration}
                                            className="text-xs text-[#9A9AA2] hover:text-[#0E0E0E]">Отменить
                                    </button>
                                </div>
                                <div className="hidden md:flex justify-end mt-4">
                                    <button onClick={cancelGeneration}
                                            className="text-sm text-[#9A9AA2] hover:text-[#0E0E0E]">Отменить
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Shell>
        );
    }

    return null;
}

// ==================== Layout helpers ====================

function Shell({children}) {
    return (
        <div
            id="sk-skazki-card-root"
            className="max-w-[1100px] mx-auto my-4 md:my-8 bg-white md:rounded-[20px] md:border md:border-[#F6F6F6] md:shadow-[0_8px_32px_rgba(14,14,14,0.06)] overflow-hidden">
            {children}
        </div>
    );
}

function Split({heading, subtitle, illustration, children}) {
    return (
        <div className="md:grid md:grid-cols-2 md:min-h-[580px]">
            {/* Left — heading + illustration. On mobile compresses to top band. */}
            <div className="sk-panel-deep px-6 pt-6 pb-4 md:px-14 md:pt-14 md:pb-10 flex flex-col md:justify-between">
                <div>
                    <h2 className="font-bold text-[#0E0E0E] tracking-[-0.02em] leading-[1.05] text-[28px] md:text-[52px] md:mb-3">
                        {heading}
                    </h2>
                    {subtitle &&
                        <p className="hidden md:block text-[#3D3D3D]/80 text-[15px] leading-[1.5] max-w-[360px]">{subtitle}</p>}
                </div>
                <div className="flex justify-center md:self-center md:mt-8 mt-4">
                    {illustration}
                </div>
            </div>

            {/* Right — form */}
            <div className="px-6 pt-4 pb-6 md:px-16 md:pt-14 md:pb-10 flex flex-col">
                <p className="md:hidden text-[13px] text-[#3D3D3D]/80 mb-4">{subtitle}</p>
                {children}
            </div>
        </div>
    );
}

function CtaRow({children}) {
    return (
        <div className="flex items-center justify-between gap-3 mt-auto pt-8">
            {children}
        </div>
    );
}

function SummaryRow({label, value, onEdit, last}) {
    return (
        <button
            onClick={onEdit}
            className={`w-full flex items-baseline justify-between py-1.5 text-left ${last ? '' : 'border-b border-dashed border-[#F6F6F6]'} hover:text-[#FF6464] transition`}
        >
            <span className="text-[#9A9AA2]">{label}</span>
            <span className="font-semibold text-[#0E0E0E] max-w-[60%] text-right truncate">{value}</span>
        </button>
    );
}


function PaymentModal({token, phase, planPrice = 490, onClose, onSuccess, onFail}) {
    const checkoutRef = useRef(null);
    // Latest-callback refs so we can ignore identity churn from the parent
    // (which re-creates onSuccess/onFail every render). Without this, every
    // parent rerender would destroy and re-mount the YooKassa iframe.
    const handlersRef = useRef({onSuccess, onFail});
    useEffect(() => {
        handlersRef.current = {onSuccess, onFail};
    }, [onSuccess, onFail]);

    // Mount imperatively from useEffect (deferred past paint) instead of from
    // a ref callback — YooKassa's render() needs the container to have settled
    // layout, and the ref-callback timing was too early in some cases.
    useEffect(() => {
        if (phase !== 'widget' || !token) return undefined;
        if (typeof window === 'undefined' || !window.YooMoneyCheckoutWidget) {
            handlersRef.current.onFail?.();
            return undefined;
        }
        const id = setTimeout(() => {
            const container = document.getElementById('skazki-yookassa-widget');
            if (!container) return;
            const checkout = new window.YooMoneyCheckoutWidget({
                confirmation_token: token,
                error_callback: () => handlersRef.current.onFail?.(),
            });
            checkout.on('success', () => handlersRef.current.onSuccess?.());
            checkout.on('fail', () => handlersRef.current.onFail?.());
            checkout.render('skazki-yookassa-widget');
            checkoutRef.current = checkout;
        }, 100);
        return () => {
            clearTimeout(id);
            if (checkoutRef.current) {
                try {
                    checkoutRef.current.destroy();
                } catch {
                }
                checkoutRef.current = null;
            }
        };
    }, [token, phase]);

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                <button
                    onClick={onClose}
                    aria-label="Закрыть"
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round"/>
                    </svg>
                </button>
                <h3 className="text-xl font-bold text-[#0E0E0E] mb-1">
                    Оплата подписки
                </h3>
                <p className="text-sm text-[#3D3D3D]/80 mb-4">
                    {planPrice} ₽/мес · отмена в один клик в любое время
                </p>
                {phase === 'polling' ? (
                    <div className="py-12 flex flex-col items-center gap-3">
                        <div
                            className="w-10 h-10 border-2 border-gray-200 border-t-[#FF6464] rounded-full animate-spin"/>
                        <p className="text-sm text-[#3D3D3D]/80">
                            Активируем подписку и открываем сказку…
                        </p>
                    </div>
                ) : (
                    <div id="skazki-yookassa-widget"/>
                )}
            </div>
        </div>
    );
}

function ErrorBlock({kind, onRetry, onBack}) {
    const copy = {
        network: {title: 'Подождите, восстанавливаем связь…', body: 'Как только вернётся — продолжим с того же места.'},
        failed: {title: 'Сегодня не получилось', body: 'Попробуем ещё раз?'},
        load: {title: 'Не удалось открыть сказку', body: 'Попробуйте снова.'},
    }[kind] || {title: 'Что-то пошло не так', body: 'Попробуем ещё раз?'};
    return (
        <div
            className="mt-6 rounded-[14px] border border-[#FFCFCC] bg-[rgba(255,100,100,0.08)] p-4 text-sm text-[#0E0E0E]">
            <div className="font-bold mb-1">{copy.title}</div>
            <div className="text-[#3D3D3D]/80">{copy.body}</div>
            <div className="flex gap-3 mt-3">
                <button onClick={onRetry} className="sk-btn sk-btn--ghost h-10 px-4 text-sm">Попробовать снова</button>
                <button onClick={onBack} className="text-sm text-[#9A9AA2] hover:text-[#0E0E0E]">Назад</button>
            </div>
        </div>
    );
}
